'use client'

import { FormField } from '@/components/form-section'
import {
    ChatAiInstructionsMode,
    ChatWithItems,
    clearChatAiInstructions,
    setChatAiInstructions,
} from '@/lib/api/atendimento/chat'
import { useEffect, useState } from 'react'
import { MdPsychology } from 'react-icons/md'
import { toast } from 'react-toastify'

/** Mesmos tetos do backend (`chat-ai-instructions.ts`). */
const MAX_LENGTH = 2000
const MAX_HOURS = 720

const MODE_OPTIONS: Array<{ mode: ChatAiInstructionsMode; title: string; description: string }> = [
    {
        mode: 'NEXT_REPLY',
        title: 'Somente na próxima resposta da IA',
        description: 'A IA usa as instruções na próxima resposta ao contato, e elas são apagadas em seguida.',
    },
    {
        mode: 'UNTIL_TIME',
        title: 'Por um período',
        description: 'Valem até a data escolhida. A IA transmite o recado uma vez, quando couber.',
    },
    {
        mode: 'PERMANENT',
        title: 'Permanente nesta conversa',
        description: 'Valem para sempre, inclusive depois de encerrar e reabrir. Use para orientações de conduta.',
    },
]

const HOUR_PRESETS = [
    { label: '4 horas', hours: 4 },
    { label: '24 horas', hours: 24 },
    { label: '3 dias', hours: 72 },
    { label: '7 dias', hours: 168 },
]

const formatDateTime = (date: Date) =>
    new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date)

/**
 * As instruções que valem agora, ou `null`. "Por um período" vencido é tratado como
 * inexistente, igual ao backend (`resolveActiveChatAiInstructions`): o registro só
 * some do banco na próxima gravação.
 */
export function getActiveAiInstructions(chat: ChatWithItems | null) {
    if (!chat?.aiInstructions?.trim() || !chat.aiInstructionsMode) return null
    if (chat.aiInstructionsMode === 'UNTIL_TIME') {
        if (!chat.aiInstructionsExpiresAt) return null
        if (new Date(chat.aiInstructionsExpiresAt).getTime() <= Date.now()) return null
    }
    return {
        text: chat.aiInstructions,
        mode: chat.aiInstructionsMode,
        expiresAt: chat.aiInstructionsExpiresAt ? new Date(chat.aiInstructionsExpiresAt) : null,
        userName: chat.aiInstructionsUserName ?? null,
        updatedAt: chat.aiInstructionsUpdatedAt ? new Date(chat.aiInstructionsUpdatedAt) : null,
    }
}

/** "na próxima resposta", "até 05/10, 14:00", "permanente". */
export function describeAiInstructionsValidity(mode: ChatAiInstructionsMode, expiresAt: Date | null) {
    if (mode === 'NEXT_REPLY') return 'na próxima resposta da IA'
    if (mode === 'UNTIL_TIME' && expiresAt) return `até ${formatDateTime(expiresAt)}`
    return 'permanente nesta conversa'
}

interface Props {
    isOpen: boolean
    chat: ChatWithItems
    onClose: () => void
    onSaved: () => void
}

/**
 * Instruções da equipe para a IA valerem SOMENTE nesta conversa — o recado que se
 * deixa com a secretária ("quando a Juliana escrever, diga a ela que...").
 *
 * A validade é obrigatória e nenhuma opção vem marcada: recado sem prazo fica
 * esquecido no chat e a IA o repete meses depois. Salvar retoma a IA pausada.
 */
export default function AiInstructionsModal({ isOpen, chat, onClose, onSaved }: Props) {
    const [text, setText] = useState('')
    const [mode, setMode] = useState<ChatAiInstructionsMode | null>(null)
    const [hours, setHours] = useState<number | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const active = getActiveAiInstructions(chat)

    useEffect(() => {
        if (!isOpen) return
        setText(active?.text ?? '')
        // "Por um período" recomeça a contar de agora ao salvar: o operador escolhe o
        // prazo de novo, em vez de herdar um número que já não significa o mesmo fim.
        setMode(active?.mode ?? null)
        setHours(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    if (!isOpen) return null

    const hoursValid = hours != null && Number.isInteger(hours) && hours >= 1 && hours <= MAX_HOURS
    const canSubmit =
        !!text.trim() && !!mode && (mode !== 'UNTIL_TIME' || hoursValid) && !submitting
    const previewEnd = mode === 'UNTIL_TIME' && hoursValid ? new Date(Date.now() + hours! * 3600000) : null

    const handleSubmit = async () => {
        if (!canSubmit || !mode) return
        setSubmitting(true)
        try {
            const res = await setChatAiInstructions(chat.id, {
                instructions: text.trim(),
                mode,
                ...(mode === 'UNTIL_TIME' ? { hours: hours! } : {}),
            })
            if (res.success) {
                toast.success(chat.aiOnHold ? 'Instruções salvas e IA retomada' : 'Instruções salvas')
                onSaved()
            } else {
                toast.error(res.error || 'Não foi possível salvar as instruções')
            }
        } catch {
            toast.error('Não foi possível salvar as instruções')
        } finally {
            setSubmitting(false)
        }
    }

    const handleRemove = async () => {
        setSubmitting(true)
        try {
            const res = await clearChatAiInstructions(chat.id)
            if (res.success) {
                toast.success('Instruções removidas')
                onSaved()
            } else {
                toast.error(res.error || 'Não foi possível remover as instruções')
            }
        } catch {
            toast.error('Não foi possível remover as instruções')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-xl">
                <h3 className="mb-1 flex items-center gap-2 text-lg font-bold">
                    <MdPsychology className="text-primary" /> Instruções para a IA
                </h3>
                <p className="mb-5 text-xs leading-relaxed text-base-content/60">
                    Valem somente nesta conversa. A IA usa o que couber no momento, não repete o que já disse
                    e nunca revela ao contato as orientações internas — só transmite o que você pedir para dizer.
                </p>

                <div className="flex flex-col gap-5">
                    {chat.aiOnHold && (
                        <div role="alert" className="alert alert-info py-2 text-xs leading-relaxed">
                            A IA está pausada nesta conversa e será retomada ao salvar. Ela responde quando o contato
                            mandar a próxima mensagem.
                        </div>
                    )}

                    <FormField
                        label="Instruções"
                        htmlFor="ai-instructions-text"
                        required
                        hint={`${text.length}/${MAX_LENGTH} caracteres`}
                    >
                        <textarea
                            id="ai-instructions-text"
                            className="textarea textarea-bordered w-full text-sm leading-relaxed"
                            rows={5}
                            maxLength={MAX_LENGTH}
                            placeholder='Ex.: "Diga à Juliana que o pedido dela já está pronto para retirada." ou "Use um tom calmo e acolhedor com este contato."'
                            value={text}
                            onChange={e => setText(e.target.value)}
                        />
                    </FormField>

                    <FormField label="Até quando manter estas instruções?" required>
                        <div className="flex flex-col gap-2">
                            {MODE_OPTIONS.map(option => (
                                <label
                                    key={option.mode}
                                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                                        mode === option.mode
                                            ? 'border-primary bg-primary/5'
                                            : 'border-base-300 hover:border-base-content/30'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="ai-instructions-mode"
                                        className="radio radio-primary radio-sm mt-0.5"
                                        checked={mode === option.mode}
                                        onChange={() => setMode(option.mode)}
                                    />
                                    <span className="flex flex-col gap-0.5">
                                        <span className="text-sm font-medium">{option.title}</span>
                                        <span className="text-xs leading-relaxed text-base-content/60">
                                            {option.description}
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </FormField>

                    {mode === 'UNTIL_TIME' && (
                        <FormField
                            label="Período"
                            htmlFor="ai-instructions-hours"
                            required
                            hint={
                                previewEnd
                                    ? `Válidas até ${formatDateTime(previewEnd)}.`
                                    : 'De 1 hora a 30 dias.'
                            }
                        >
                            <div className="flex flex-wrap gap-2">
                                {HOUR_PRESETS.map(preset => (
                                    <button
                                        key={preset.hours}
                                        type="button"
                                        className={`btn btn-sm ${hours === preset.hours ? 'btn-primary' : 'btn-outline'}`}
                                        onClick={() => setHours(preset.hours)}
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                            <div className="join w-full max-w-xs">
                                <input
                                    id="ai-instructions-hours"
                                    type="number"
                                    min={1}
                                    max={MAX_HOURS}
                                    step={1}
                                    className={`input input-bordered input-sm join-item w-full ${
                                        hours != null && !hoursValid ? 'input-error' : ''
                                    }`}
                                    placeholder="Outro valor"
                                    value={hours ?? ''}
                                    onChange={e => setHours(e.target.value === '' ? null : Number(e.target.value))}
                                />
                                <span className="join-item flex items-center border border-base-300 bg-base-200 px-3 text-xs">
                                    horas
                                </span>
                            </div>
                        </FormField>
                    )}
                </div>

                <div className="modal-action items-center">
                    {active && (
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm mr-auto text-error"
                            onClick={handleRemove}
                            disabled={submitting}
                        >
                            Remover instruções
                        </button>
                    )}
                    <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={!canSubmit}>
                        {submitting ? <span className="loading loading-spinner loading-xs" /> : 'Salvar'}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={() => !submitting && onClose()} />
        </div>
    )
}
